import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, LayoutGrid, Table2, Linkedin, Mail, Bell, Search } from "lucide-react";
import { api } from "@/lib/api";

// Each contact type has its own stages, company label, and extra fields.
const TYPES = {
  investor: {
    label: "Investors",
    singular: "investor",
    companyLabel: "Firm / Company",
    extra: [],
    stages: [
      { id: "Prospect", color: "#8f8f8f" },
      { id: "Reached Out", color: "#2864dc" },
      { id: "Connected", color: "#d99a28" },
      { id: "In Diligence", color: "#8c3cc8" },
      { id: "Invested", color: "#22b455" },
      { id: "Passed", color: "#c83232" },
    ],
  },
  developer: {
    label: "Developers",
    singular: "developer",
    companyLabel: "Company",
    extra: [{ key: "role", label: "Role / Stack", placeholder: "Full-stack · React, Node" }],
    stages: [
      { id: "Prospect", color: "#8f8f8f" },
      { id: "Reached Out", color: "#2864dc" },
      { id: "Connected", color: "#d99a28" },
      { id: "Interviewing", color: "#8c3cc8" },
      { id: "Hired", color: "#22b455" },
      { id: "Passed", color: "#c83232" },
    ],
  },
  talent: {
    label: "Talent",
    singular: "talent",
    companyLabel: "Current company",
    extra: [{ key: "position", label: "Position", placeholder: "Head of Community" }],
    stages: [
      { id: "Prospect", color: "#8f8f8f" },
      { id: "Reached Out", color: "#2864dc" },
      { id: "Connected", color: "#d99a28" },
      { id: "Interviewing", color: "#8c3cc8" },
      { id: "Joined", color: "#22b455" },
      { id: "Passed", color: "#c83232" },
    ],
  },
};
const TYPE_ORDER = ["investor", "developer", "talent"];
const SEARCH_KEYS = ["name", "company", "email", "phone", "notes", "stage", "linkedin", "role", "position", "location"];

const inputCls = "w-full bg-[#141414] border border-[#2a2a2a] px-3.5 py-2.5 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";
const labelCls = "block font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] mb-1.5";
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const todayStr = () => new Date().toISOString().slice(0, 10);
const isOverdue = (d) => d && d <= todayStr();
const extraText = (type, c) => TYPES[type].extra.map((f) => c[f.key]).filter(Boolean).join(" · ");

function ContactModal({ type, initial, onClose, onSaved }) {
  const cfg = TYPES[type];
  const blank = { type, name: "", email: "", company: "", stage: cfg.stages[0].id, notes: "", last_contact: "", linkedin: "", followup_date: "", phone: "", role: "", location: "", position: "" };
  const [form, setForm] = useState({ ...blank, ...(initial || {}) });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) await api.put(`/admin/crm/contacts/${initial.id}`, form);
      else await api.post(`/admin/crm/contacts`, form);
      toast.success(initial?.id ? "Contact updated" : "Contact added");
      onSaved();
    } catch { toast.error("Failed to save contact"); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-5 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-[#222] rounded w-full max-w-lg p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-pf text-[24px] text-[#f0f0f0]">{initial?.id ? "Edit" : "New"} {cfg.singular}</h3>
          <button onClick={onClose} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Name</label><input value={form.name} onChange={upd("name")} placeholder="Full name" className={inputCls} data-testid="crm-name" /></div>
            <div><label className={labelCls}>{cfg.companyLabel}</label><input value={form.company} onChange={upd("company")} className={inputCls} data-testid="crm-company" /></div>
          </div>
          {cfg.extra.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {cfg.extra.map((f) => (
                <div key={f.key}><label className={labelCls}>{f.label}</label><input value={form[f.key] || ""} onChange={upd(f.key)} placeholder={f.placeholder} className={inputCls} data-testid={`crm-${f.key}`} /></div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={upd("email")} className={inputCls} data-testid="crm-email" /></div>
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={upd("phone")} className={inputCls} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>LinkedIn URL</label><input value={form.linkedin} onChange={upd("linkedin")} placeholder="linkedin.com/in/…" className={inputCls} data-testid="crm-linkedin" /></div>
            <div>
              <label className={labelCls}>Stage</label>
              <select value={form.stage} onChange={upd("stage")} className={inputCls} data-testid="crm-stage">
                {cfg.stages.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Last contact</label><input type="date" value={form.last_contact || ""} onChange={upd("last_contact")} className={inputCls} /></div>
            <div><label className={labelCls}>Follow-up reminder</label><input type="date" value={form.followup_date || ""} onChange={upd("followup_date")} className={inputCls} data-testid="crm-followup" /></div>
          </div>
          <div><label className={labelCls}>Notes</label><textarea rows={3} value={form.notes} onChange={upd("notes")} className={inputCls} /></div>
          <div className="flex gap-2 pt-1">
            <button type="submit" className="bg-[#4deb7a] text-[#080808] font-medium text-[13px] px-5 py-2.5 rounded" data-testid="crm-save">{initial?.id ? "Save changes" : "Add contact"}</button>
            <button type="button" onClick={onClose} className="text-[13px] text-[#bdbdbd] px-3">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Card({ c, type, onEdit, onDelete, onDragStart, dragging }) {
  const highlight = extraText(type, c);
  return (
    <div draggable onDragStart={onDragStart}
      className={`group bg-[#0e0e0e] border border-[#222] rounded p-3.5 hover:border-[#2a2a2a] transition-all cursor-grab active:cursor-grabbing ${dragging ? "opacity-40" : ""}`}
      data-testid={`crm-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <div className="text-[13px] text-[#f0f0f0] font-medium truncate">{c.name || "Untitled contact"}</div>
          {c.company && <div className="text-[11px] text-[#bdbdbd] truncate">{c.company}</div>}
        </div>
        <div className="flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
          <button data-testid={`crm-edit-${c.id}`} onClick={onEdit} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`crm-delete-${c.id}`} onClick={onDelete} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {highlight && <div className="text-[11px] text-[#4deb7a] mb-2 leading-snug">{highlight}</div>}
      {c.notes && <div className="text-[11px] text-[#d4d4d4] leading-snug mb-2 line-clamp-2">{c.notes}</div>}
      <div className="flex items-center gap-2.5 text-[#8f8f8f]">
        {c.email && <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#4deb7a]" title={c.email}><Mail className="h-3.5 w-3.5" /></a>}
        {c.linkedin && <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-[#4deb7a]" title="LinkedIn"><Linkedin className="h-3.5 w-3.5" /></a>}
        {c.followup_date && (
          <span className={`ml-auto flex items-center gap-1 font-mono-dm text-[10px] ${isOverdue(c.followup_date) ? "text-[#dc6428]" : "text-[#8f8f8f]"}`}>
            <Bell className="h-3 w-3" /> {fmtDate(c.followup_date)}
          </span>
        )}
      </div>
    </div>
  );
}

export default function CRM() {
  const [type, setType] = useState("investor");
  const [view, setView] = useState("board");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [query, setQuery] = useState("");
  const cfg = TYPES[type];
  const stages = cfg.stages;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/contacts?type=${type}`);
      setContacts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setQuery(""); }, [type]);

  const del = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/admin/crm/contacts/${id}`);
    toast.success("Contact deleted");
    load();
  };

  const dropToStage = async (stage) => {
    if (!dragId) return;
    const c = contacts.find((x) => x.id === dragId);
    setDragId(null);
    if (!c || c.stage === stage) return;
    const count = contacts.filter((x) => x.stage === stage).length;
    setContacts((prev) => prev.map((x) => (x.id === dragId ? { ...x, stage } : x)));
    try {
      await api.put(`/admin/crm/contacts/${dragId}/move`, { stage, sort_order: count });
      load();
    } catch { toast.error("Failed to move"); load(); }
  };

  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = contacts.filter((c) => {
    const hay = SEARCH_KEYS.map((k) => c[k] || "").join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const byStage = (stage) => shown.filter((c) => c.stage === stage);
  const counts = stages.reduce((a, s) => ({ ...a, [s.id]: byStage(s.id).length }), {});
  const extraLabel = cfg.extra.map((e) => e.label).join(" / ") || "Details";

  return (
    <div data-testid="crm-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="mono-eyebrow text-[#4deb7a] mb-2">Relationship CRM</div>
          <h1 className="font-serif-pf text-[26px] sm:text-[28px] text-[#f0f0f0]">Pipeline</h1>
        </div>
        <button data-testid="crm-add-btn" onClick={() => setModal({ new: true })} className="inline-flex items-center gap-2 bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] text-[12px] font-medium px-4 py-2.5 rounded transition-colors">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add {cfg.singular}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex flex-wrap border border-[#222] rounded overflow-hidden">
          {TYPE_ORDER.map((t) => (
            <button key={t} data-testid={`crm-board-${t}`} onClick={() => setType(t)}
              className={`px-4 py-2 text-[12px] font-medium transition-colors ${type === t ? "bg-[#4deb7a] text-[#080808]" : "text-[#d4d4d4] hover:bg-[#141414]"}`}>
              {TYPES[t].label}
            </button>
          ))}
        </div>
        <div className="inline-flex border border-[#222] rounded overflow-hidden">
          <button data-testid="crm-view-board" onClick={() => setView("board")} className={`px-3 py-2 flex items-center gap-1.5 text-[12px] transition-colors ${view === "board" ? "bg-[#141414] text-[#4deb7a]" : "text-[#8f8f8f] hover:text-[#d4d4d4]"}`}><LayoutGrid className="h-3.5 w-3.5" /> Board</button>
          <button data-testid="crm-view-table" onClick={() => setView("table")} className={`px-3 py-2 flex items-center gap-1.5 text-[12px] transition-colors ${view === "table" ? "bg-[#141414] text-[#4deb7a]" : "text-[#8f8f8f] hover:text-[#d4d4d4]"}`}><Table2 className="h-3.5 w-3.5" /> Table</button>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-[380px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8f8f8f]" strokeWidth={1.5} />
          <input data-testid="crm-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${cfg.label.toLowerCase()} by name, company, notes…`}
            className="w-full bg-[#0e0e0e] border border-[#222] rounded pl-9 pr-8 py-2 text-[12px] text-[#e8e8e8] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#4deb7a] transition-colors" />
          {query && <button data-testid="crm-search-clear" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8f8f8f] hover:text-[#e8e8e8]"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {query && <span data-testid="crm-search-count" className="font-mono-dm text-[10px] text-[#8f8f8f]">{shown.length} of {contacts.length}</span>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#8f8f8f] py-20 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : view === "board" ? (
        <>
        {query && shown.length === 0 && <div data-testid="crm-board-no-match" className="mb-3 text-[13px] text-[#8f8f8f]">No contacts match your search.</div>}
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1">
          {stages.map((s) => (
            <div key={s.id} onDragOver={(e) => e.preventDefault()} onDrop={() => dropToStage(s.id)}
              className="w-[240px] min-w-[240px] bg-[#0b0b0b] border border-[#1a1a1a] rounded-md p-2.5" data-testid={`crm-col-${s.id}`}>
              <div className="flex items-center gap-2 px-1.5 py-1 mb-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-[12px] text-[#e8e8e8] font-medium flex-1">{s.id}</span>
                <span className="font-mono-dm text-[10px] text-[#8f8f8f]">{counts[s.id]}</span>
              </div>
              <div className="flex flex-col gap-2 min-h-[40px]">
                {byStage(s.id).map((c) => (
                  <Card key={c.id} c={c} type={type} dragging={dragId === c.id}
                    onDragStart={() => setDragId(c.id)}
                    onEdit={() => setModal({ contact: c })}
                    onDelete={() => del(c.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
      ) : (
        <div className="border-t border-[#222] overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-4 font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] px-4 py-2 border-b border-[#222]">
              <span>Name</span><span>{cfg.companyLabel}</span><span>Stage</span><span>{extraLabel}</span><span>Follow-up</span>
            </div>
            {shown.length === 0 && <div className="px-4 py-8 text-[13px] text-[#8f8f8f]">{query ? "No contacts match your search." : "No contacts yet. Add your first one."}</div>}
            {shown.map((c) => {
              const st = stages.find((s) => s.id === c.stage) || stages[0];
              return (
                <div key={c.id} className="group grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-4 items-center px-4 py-3 border-b border-[#141414] hover:bg-[#0e0e0e] transition-colors" data-testid={`crm-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#f0f0f0] truncate flex items-center gap-2">
                      {c.name || "Untitled contact"}
                      {c.linkedin && <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-[#8f8f8f] hover:text-[#4deb7a]"><Linkedin className="h-3 w-3" /></a>}
                    </div>
                    {c.email && <div className="text-[11px] text-[#8f8f8f] truncate">{c.email}</div>}
                  </div>
                  <span className="text-[12px] text-[#d4d4d4] truncate">{c.company || "—"}</span>
                  <span><span className="inline-flex items-center gap-1.5 font-mono-dm text-[10px] px-2 py-0.5 rounded-sm border" style={{ color: st.color, borderColor: st.color + "55", background: st.color + "14" }}>{c.stage}</span></span>
                  <span className="text-[12px] text-[#d4d4d4] truncate">{extraText(type, c) || "—"}</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono-dm text-[10px] ${isOverdue(c.followup_date) ? "text-[#dc6428]" : "text-[#8f8f8f]"}`}>{c.followup_date ? fmtDate(c.followup_date) : "—"}</span>
                    <div className="flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setModal({ contact: c })} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(c.id)} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modal && (
        <ContactModal type={type} initial={modal.contact} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
